/**
 * محرك التكرار.
 *
 * مبدأ التصميم:
 *  - كل الحسابات تجري على "تاريخ محلي" (YYYY-MM-DD) لمنطقة زمنية محددة،
 *    ثم يُحوَّل إلى لحظة UTC عند الحاجة فقط. هذا يجعل التعامل مع اختلاف عدد
 *    أيام الأشهر والتوقيت الصيفي صحيحًا، ويجعل توليد المواعيد حتميًا
 *    (deterministic) فيمكن منع التكرار عبر مفتاح الموعد.
 *  - الدوال نقية بالكامل: (rule + نطاق تواريخ) ← قائمة تواريخ. لا تعتمد على
 *    ساعة النظام ولا على قاعدة البيانات، لذلك تُختبر بسهولة.
 */

import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import type { Instant, LocalDate, LocalTime, TimeZone } from './ids';
import { occurrenceKey } from './ids';
import type { RecurrenceFrequency, RecurrenceRule } from './types';

export const MAX_OCCURRENCES_PER_RUN = 500;
export const MAX_FLEXIBLE_OCCURRENCES_PER_RUN = 10_000;
export const DEFAULT_RECURRENCE: RecurrenceRule = {
  frequency: 'once',
  weekdays: [],
  interval: 1,
  monthDay: null,
  excludeDates: [],
  untilDate: null,
  maxOccurrences: null,
  windows: [],
};

export function normalizeRule(input: Partial<RecurrenceRule> | null | undefined): RecurrenceRule {
  const base = { ...DEFAULT_RECURRENCE, ...(input ?? {}) };
  return {
    frequency: base.frequency,
    weekdays: [...new Set(base.weekdays)].filter((d) => d >= 0 && d <= 6).sort(),
    interval: Math.max(1, Math.floor(base.interval || 1)),
    monthDay: base.monthDay === null || base.monthDay === undefined ? null : clamp(base.monthDay, 1, 31),
    excludeDates: [...new Set(base.excludeDates)].sort(),
    untilDate: base.untilDate ?? null,
    maxOccurrences:
      base.maxOccurrences === null || base.maxOccurrences === undefined
        ? null
        : Math.max(1, Math.floor(base.maxOccurrences)),
    windows: Array.isArray(base.windows)
      ? base.windows.map((window) => ({
          weekdays: [...new Set(window.weekdays ?? [])].filter((day) => day >= 0 && day <= 6).sort(),
          startTime: window.startTime,
          endTime: window.endTime,
          intervalMinutes: Math.max(1, Math.floor(window.intervalMinutes || 1)),
        }))
      : [],
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export interface RuleValidationIssue {
  field: keyof RecurrenceRule | 'startDate';
  message: string;
}

export function validateRule(rule: RecurrenceRule, startDate: LocalDate): RuleValidationIssue[] {
  const issues: RuleValidationIssue[] = [];
  if (!isValidLocalDate(startDate)) {
    issues.push({ field: 'startDate', message: 'تاريخ البدء غير صالح' });
  }
  switch (rule.frequency) {
    case 'once':
      break;
    case 'daily':
    case 'interval_days':
      break;
    case 'flexible':
      if (!rule.windows?.length) {
        issues.push({ field: 'frequency', message: 'أضف فترة واحدة على الأقل للجدول المرن' });
        break;
      }
      rule.windows.forEach((window, index) => {
        if (window.weekdays.length === 0) {
          issues.push({ field: 'windows', message: `اختر يومًا واحدًا على الأقل للفترة ${index + 1}` });
        }
        if (!isValidLocalTime(window.startTime) || !isValidLocalTime(window.endTime)) {
          issues.push({ field: 'windows', message: `وقت الفترة ${index + 1} غير صالح` });
        }
        if (!Number.isInteger(window.intervalMinutes) || window.intervalMinutes < 1 || window.intervalMinutes > 1440) {
          issues.push({ field: 'windows', message: `الفاصل في الفترة ${index + 1} يجب أن يكون بين 1 و1440 دقيقة` });
        }
      });
      break;
    case 'weekly':
    case 'interval_weeks':
      if (rule.weekdays.length === 0) {
        issues.push({ field: 'weekdays', message: 'اختر يومًا واحدًا على الأقل من أيام الأسبوع' });
      }
      break;
    case 'monthly_day':
      if (rule.monthDay === null) {
        issues.push({ field: 'monthDay', message: 'حدد يوم الشهر' });
      }
      break;
    case 'custom':
      if (rule.weekdays.length === 0 && rule.monthDay === null) {
        issues.push({ field: 'weekdays', message: 'الجدول المخصص يحتاج أيامًا أو يوم شهر' });
      }
      break;
    default:
      issues.push({ field: 'frequency', message: 'نوع تكرار غير معروف' });
  }
  if (rule.interval < 1) issues.push({ field: 'interval', message: 'الفاصل يجب أن يكون 1 على الأقل' });
  if (rule.untilDate && isValidLocalDate(startDate) && rule.untilDate < startDate) {
    issues.push({ field: 'untilDate', message: 'تاريخ الانتهاء قبل تاريخ البدء' });
  }
  return issues;
}

/* =============================================================================
 * أدوات التاريخ المحلي
 * ========================================================================== */

const LOCAL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const LOCAL_TIME_RE = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

export function isValidLocalTime(value: string): boolean {
  return LOCAL_TIME_RE.test(value);
}

export function isValidLocalDate(value: string): boolean {
  if (!LOCAL_DATE_RE.test(value)) return false;
  const d = parseLocalDate(value);
  return d !== null && toLocalDate(d) === value;
}

/** تفسير YYYY-MM-DD إلى Date عند منتصف ليل UTC (آمن للحساب، ليس للعرض). */
export function parseLocalDate(value: LocalDate): Date | null {
  if (!LOCAL_DATE_RE.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) {
    return null;
  }
  return date;
}

export function toLocalDate(date: Date): LocalDate {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addDaysLocal(value: LocalDate, days: number): LocalDate {
  const d = parseLocalDate(value);
  if (!d) throw new Error(`تاريخ غير صالح: ${value}`);
  d.setUTCDate(d.getUTCDate() + days);
  return toLocalDate(d);
}

export function diffDaysLocal(from: LocalDate, to: LocalDate): number {
  const a = parseLocalDate(from);
  const b = parseLocalDate(to);
  if (!a || !b) throw new Error('تاريخ غير صالح في حساب الفرق');
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/** 0 = الأحد ... 6 = السبت (مطابق لجدول العمل في العضوية) */
export function weekdayOfLocalDate(value: LocalDate): number {
  const d = parseLocalDate(value);
  if (!d) throw new Error(`تاريخ غير صالح: ${value}`);
  return d.getUTCDay();
}

export function dayOfMonth(value: LocalDate): number {
  const d = parseLocalDate(value);
  if (!d) throw new Error(`تاريخ غير صالح: ${value}`);
  return d.getUTCDate();
}

/** اليوم المحلي الحالي لمنطقة زمنية معيّنة. */
export function todayInTimeZone(timeZone: TimeZone, now: Date = new Date()): LocalDate {
  return formatInTimeZone(now, timeZone, 'yyyy-MM-dd');
}

/** تحويل (تاريخ محلي + وقت محلي) إلى لحظة UTC في المنطقة الزمنية المعطاة. */
export function toInstant(localDate: LocalDate, localTime: LocalTime, timeZone: TimeZone): Instant {
  const value = `${localDate}T${normalizeTime(localTime)}:00`;
  return fromZonedTime(value, timeZone).toISOString();
}

function normalizeTime(time: LocalTime): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!m) return '00:00';
  const hh = String(clamp(Number(m[1]), 0, 23)).padStart(2, '0');
  const mm = String(clamp(Number(m[2]), 0, 59)).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function plusMinutes(instant: Instant, minutes: number): Instant {
  return new Date(new Date(instant).getTime() + minutes * 60_000).toISOString();
}

/* =============================================================================
 * المطابقة والتوليد
 * ========================================================================== */

/** هل يطابق تاريخ محلي القاعدة؟ (بدون اعتبار الاستثناءات والحدود) */
export function matchesRule(rule: RecurrenceRule, startDate: LocalDate, date: LocalDate): boolean {
  if (date < startDate) return false;
  const offset = diffDaysLocal(startDate, date);

  switch (rule.frequency) {
    case 'once':
      return offset === 0;
    case 'daily':
      return true;
    case 'flexible':
      return Boolean(rule.windows?.some((window) => window.weekdays.includes(weekdayOfLocalDate(date))));
    case 'interval_days':
      return offset % rule.interval === 0;
    case 'weekly':
      return rule.weekdays.includes(weekdayOfLocalDate(date));
    case 'interval_weeks': {
      const weekIndex = Math.floor(offset / 7);
      if (weekIndex % rule.interval !== 0) return false;
      return rule.weekdays.includes(weekdayOfLocalDate(date));
    }
    case 'monthly_day':
      return rule.monthDay !== null && dayOfMonth(date) === rule.monthDay;
    case 'custom': {
      if (rule.interval > 1 && offset % rule.interval !== 0) return false;
      const weekdayOk = rule.weekdays.length === 0 || rule.weekdays.includes(weekdayOfLocalDate(date));
      const monthDayOk = rule.monthDay === null || dayOfMonth(date) === rule.monthDay;
      return weekdayOk && monthDayOk;
    }
    default:
      return false;
  }
}

export interface GenerateInput {
  rule: RecurrenceRule;
  startDate: LocalDate;
  /** أول تاريخ محلي مطلوب توليده (شامل) */
  fromDate: LocalDate;
  /** آخر تاريخ محلي مطلوب توليده (شامل) */
  throughDate: LocalDate;
  /** حد أعلى للنتائج في التشغيل الواحد */
  limit?: number;
}

/**
 * توليد المواعيد المستحقة داخل نطاق محدد.
 * حتمي بالكامل: نفس المدخلات تعطي نفس المخرجات، ويُستخدم occurrenceKey
 * لمنع إنشاء سجل تنفيذ مكرر عند إعادة تشغيل المهمة المجدولة.
 */
export function generateOccurrences(input: GenerateInput): LocalDate[] {
  const { rule, startDate, fromDate, throughDate } = input;
  const limit = Math.min(input.limit ?? MAX_OCCURRENCES_PER_RUN, MAX_OCCURRENCES_PER_RUN);

  if (throughDate < startDate) return [];
  if (throughDate < fromDate) return [];

  // تاريخ البداية الفعلي لحساب العدّ (maxOccurrences) هو بداية القاعدة.
  // حدّ المسح لا يتجاوز untilDate عندما تكون ماضية.
  const scanEnd = rule.untilDate !== null && rule.untilDate < throughDate ? rule.untilDate : throughDate;
  const excluded = new Set(rule.excludeDates);
  const results: LocalDate[] = [];
  let matchedCount = 0;

  let cursor = startDate;
  let guard = 0;
  const maxScanDays = 366 * 10;
  while (cursor <= scanEnd) {
    if (guard++ > maxScanDays) break;
    if (matchesRule(rule, startDate, cursor) && !excluded.has(cursor)) {
      matchedCount += 1;
      const withinMax = rule.maxOccurrences === null || matchedCount <= rule.maxOccurrences;
      if (withinMax && cursor >= fromDate && results.length < limit) {
        results.push(cursor);
      }
    }
    cursor = addDaysLocal(cursor, 1);
  }
  return results;
}

/** وصف مقروء للقاعدة بالعربية. */
export const FREQUENCY_LABELS: Record<RecurrenceFrequency, string> = {
  once: 'مرة واحدة',
  daily: 'يوميًا',
  weekly: 'أسبوعيًا في أيام محددة',
  interval_days: 'كل عدد محدد من الأيام',
  interval_weeks: 'كل عدد محدد من الأسابيع',
  monthly_day: 'شهريًا في يوم محدد',
  custom: 'جدول مخصص',
  flexible: 'جدول مرن داخل اليوم',
};

export const WEEKDAY_LABELS = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

export function describeRule(rule: RecurrenceRule): string {
  const parts: string[] = [FREQUENCY_LABELS[rule.frequency]];
  switch (rule.frequency) {
    case 'interval_days':
      parts.push(`كل ${rule.interval} يوم`);
      break;
    case 'interval_weeks':
      parts.push(`كل ${rule.interval} أسبوع`);
      if (rule.weekdays.length) parts.push(rule.weekdays.map((d) => WEEKDAY_LABELS[d]).join('، '));
      break;
    case 'weekly':
      if (rule.weekdays.length) parts.push(rule.weekdays.map((d) => WEEKDAY_LABELS[d]).join('، '));
      break;
    case 'monthly_day':
      parts.push(`يوم ${rule.monthDay ?? '-'} من الشهر`);
      break;
    case 'custom':
      if (rule.weekdays.length) parts.push(rule.weekdays.map((d) => WEEKDAY_LABELS[d]).join('، '));
      if (rule.monthDay !== null) parts.push(`يوم ${rule.monthDay}`);
      break;
    case 'flexible':
      parts.push(`${rule.windows?.length ?? 0} فترة داخل اليوم`);
      break;
    default:
      break;
  }
  if (rule.untilDate) parts.push(`حتى ${rule.untilDate}`);
  if (rule.maxOccurrences) parts.push(`بحد ${rule.maxOccurrences} موعد`);
  return parts.join(' — ');
}

export interface PlannedOccurrence {
  localDate: LocalDate;
  localTime?: LocalTime;
  scheduleDate?: LocalDate;
  occurrenceKey: string;
  startAt: Instant;
  dueAt: Instant;
}

/** تحويل المواعيد المحلية إلى سجلات تنفيذ مخططة جاهزة للإدراج. */
export function planOccurrences(input: {
  templateId: string;
  rule: RecurrenceRule;
  startDate: LocalDate;
  startTime: LocalTime;
  durationMinutes: number;
  timeZone: TimeZone;
  fromDate: LocalDate;
  throughDate: LocalDate;
  limit?: number;
}): PlannedOccurrence[] {
  const dates = generateOccurrences({
    rule: input.rule,
    startDate: input.startDate,
    fromDate: input.fromDate,
    throughDate: input.throughDate,
    ...(input.limit === undefined ? {} : { limit: input.limit }),
  });
  if (input.rule.frequency === 'flexible') {
    const windows = input.rule.windows ?? [];
    const planned: PlannedOccurrence[] = [];
    const maxPlanned = Math.min(input.limit ?? MAX_FLEXIBLE_OCCURRENCES_PER_RUN, MAX_FLEXIBLE_OCCURRENCES_PER_RUN);
    for (const localDate of dates) {
      const weekday = weekdayOfLocalDate(localDate);
      const dayPlan = new Map<string, PlannedOccurrence>();
      for (const window of windows) {
        if (!window.weekdays.includes(weekday)) continue;
        const [startHour, startMinute] = window.startTime.split(':').map(Number) as [number, number];
        const [endHour, endMinute] = window.endTime.split(':').map(Number) as [number, number];
        const startMinutes = startHour * 60 + startMinute;
        let endMinutes = endHour * 60 + endMinute;
        if (endMinutes <= startMinutes) endMinutes += 24 * 60;
        for (let minute = startMinutes; minute < endMinutes; minute += window.intervalMinutes) {
          const minuteOfDay = minute % (24 * 60);
          const occurrenceDate = minute >= 24 * 60 ? addDaysLocal(localDate, 1) : localDate;
          const localTime = `${String(Math.floor(minuteOfDay / 60)).padStart(2, '0')}:${String(minuteOfDay % 60).padStart(2, '0')}`;
          const startAt = toInstant(occurrenceDate, localTime, input.timeZone);
          const key = `${occurrenceKey(input.templateId, occurrenceDate)}:${localTime.replace(':', '')}`;
          dayPlan.set(key, {
            localDate: occurrenceDate,
            localTime,
            scheduleDate: localDate,
            occurrenceKey: key,
            startAt,
            dueAt: plusMinutes(startAt, input.durationMinutes),
          });
        }
      }
      if (planned.length + dayPlan.size > maxPlanned) break;
      planned.push(...dayPlan.values());
      if (planned.length >= maxPlanned) break;
    }
    return planned.sort((a, b) => a.startAt.localeCompare(b.startAt));
  }
  return dates.map((localDate) => {
    const startAt = toInstant(localDate, input.startTime, input.timeZone);
    return {
      localDate,
      occurrenceKey: occurrenceKey(input.templateId, localDate),
      startAt,
      dueAt: plusMinutes(startAt, input.durationMinutes),
    };
  });
}
